import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp, Download, ExternalLink, Eye, RefreshCw, Search, Sparkles, Upload } from "lucide-react";

function formatDateTime(value) {
  if (!value) return "Never";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusColor(status) {
  switch (status) {
    case "completed":
    case "created":
    case "updated":
      return "green";
    case "running":
    case "processing":
    case "applying":
      return "blue";
    case "failed":
    case "invalid":
      return "red";
    case "budget_exhausted":
      return "orange";
    case "duplicate":
    case "review_ready":
      return "orange";
    case "valid":
    case "uploaded":
      return "teal";
    case "scanning":
      return "blue";
    case "pending":
      return "yellow";
    case "skipped":
      return "gray";
    default:
      return "gray";
  }
}

function titleize(value) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function detectionStatusLabel(lead) {
  if (!lead.website_url && lead.jobber_detection_status === "skipped") return "No website";

  return titleize(lead.jobber_detection_status || "");
}

function normalizedHttpUrl(value) {
  if (!value) return null;

  const rawValue = String(value).trim();
  if (!rawValue) return null;

  try {
    const url = new URL(rawValue.startsWith("http://") || rawValue.startsWith("https://") ? rawValue : `https://${rawValue}`);
    if (!["http:", "https:"].includes(url.protocol)) return null;

    return url.toString();
  } catch {
    return null;
  }
}

function displayUrl(value) {
  const normalizedUrl = normalizedHttpUrl(value);
  if (!normalizedUrl) return String(value || "");

  return normalizedUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function optionalValue(value) {
  return value === null || value === undefined || value === "" ? "Unknown" : value;
}

function booleanLabel(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Unknown";
}

function confidenceLabel(value) {
  if (value === null || value === undefined || value === "") return "Unknown";
  return `${Math.round(Number(value) * 100)}%`;
}

const DEFAULT_FILTERS = {
  q: "",
  campaign_id: "",
  industry: "",
  region: "",
  jobber: "",
  service_m8: "",
  detection_status: "",
  enrichment_status: "",
  lead_tier: "",
  sales_status: "",
  has_primary_contact: "",
  contact_quality: "",
  last_contacted: "",
  follow_up: "",
  never_contacted: "",
  enrichment_industry: "",
  multiple_crews: "",
  field_staff_min: "",
  website: "",
  sort: "score",
};

export default function Leads() {
  const [payload, setPayload] = useState(null);
  const [campaignPayload, setCampaignPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rescanningId, setRescanningId] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [batchEnriching, setBatchEnriching] = useState(null);
  const [expandedEvidenceId, setExpandedEvidenceId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const toast = useToast();

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("page", page);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });

      const response = await fetch(`/api/leads?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to load leads");

      setPayload(data);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const response = await fetch("/api/lead_campaigns", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to load campaigns");

      setCampaignPayload(data);
    } catch (err) {
      toast({
        title: "Failed to load campaigns",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCampaignsLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, [page, filters]);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setPage(1);
    setFilters(DEFAULT_FILTERS);
  };

  const rescanLead = async (leadId) => {
    try {
      setRescanningId(leadId);
      const response = await fetch(`/api/leads/${leadId}/detect_jobber`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to start scan");

      toast({
        title: "Website scan started",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      loadLeads();
    } catch (err) {
      toast({
        title: "Failed to start website scan",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRescanningId(null);
    }
  };

  const enrichLead = async (leadId, force = false) => {
    try {
      setEnrichingId(leadId);
      const params = force ? "?force=true" : "";
      const response = await fetch(`/api/leads/${leadId}/enrich${params}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to start enrichment");

      toast({
        title: "Lead enrichment started",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      loadLeads();
    } catch (err) {
      toast({
        title: "Failed to start enrichment",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEnrichingId(null);
    }
  };

  const runEnrichmentBatch = async (action) => {
    try {
      setBatchEnriching(action);
      const response = await fetch(`/api/leads/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to start enrichment batch");

      toast({
        title: "Enrichment batch started",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadLeads();
    } catch (err) {
      toast({
        title: "Failed to start enrichment batch",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBatchEnriching(null);
    }
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    window.location.href = `/api/leads/download_csv?${params.toString()}`;
  };

  if ((loading && !payload) || (campaignsLoading && !campaignPayload)) {
    return (
      <Box maxW="1500px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <VStack spacing={3}>
          <Spinner color="blue.500" />
          <Text color="gray.600">Loading leads...</Text>
        </VStack>
      </Box>
    );
  }

  const leads = payload?.leads || [];
  const summary = payload?.summary || {};
  const pagination = payload?.pagination || { page: 1, total_pages: 0 };
  const campaigns = campaignPayload?.campaigns || [];
  const options = campaignPayload?.options || {};
  const industries = options.industries || [];
  const regions = options.regions || [];
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => value && !(key === "sort" && value === DEFAULT_FILTERS.sort)).length;
  const filterLabelProps = {
    mb: 1,
    color: "gray.600",
    fontSize: "xs",
    fontWeight: "semibold",
    textTransform: "uppercase",
    letterSpacing: "0",
  };
  const filterInputProps = {
    bg: "white",
    borderColor: "gray.300",
    size: "sm",
  };

  return (
    <Box maxW="1500px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="start" spacing={4}>
          <Box>
            <Heading size="lg">Leads</Heading>
            <Text mt={1} color="gray.600" fontSize="sm">
              Lead index with Jobber and ServiceM8 website detection.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button
              variant="outline"
              leftIcon={batchEnriching === "retry_failed_enrichments" ? <Spinner size="xs" /> : <RefreshCw size={16} />}
              onClick={() => runEnrichmentBatch("retry_failed_enrichments")}
              isDisabled={!!batchEnriching}
            >
              Retry Failed
            </Button>
            <Button
              variant="outline"
              leftIcon={batchEnriching === "enrich_jobber_positive" ? <Spinner size="xs" /> : <Sparkles size={16} />}
              onClick={() => runEnrichmentBatch("enrich_jobber_positive")}
              isDisabled={!!batchEnriching}
            >
              Enrich Jobber Leads
            </Button>
            <Button variant="outline" leftIcon={<Download size={16} />} onClick={downloadCsv}>
              Download CSV
            </Button>
            <Button as={RouterLink} to="/lead-imports" variant="outline" leftIcon={<Upload size={16} />}>
              CSV Imports
            </Button>
            <Button as={RouterLink} to="/google-places" colorScheme="blue" leftIcon={<Search size={16} />}>
              Google Places
            </Button>
          </HStack>
        </HStack>

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" }} gap={3}>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Total</StatLabel>
            <StatNumber>{summary.total || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Jobber Positive</StatLabel>
            <StatNumber>{summary.jobber_positive || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>ServiceM8 Positive</StatLabel>
            <StatNumber>{summary.service_m8_positive || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>With Website</StatLabel>
            <StatNumber>{summary.with_website || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Pending</StatLabel>
            <StatNumber>{summary.pending_detection || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Failed</StatLabel>
            <StatNumber>{summary.failed_detection || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Enriched</StatLabel>
            <StatNumber>{summary.enrichment_completed || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Tier A</StatLabel>
            <StatNumber>{summary.tier_a || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Tier B</StatLabel>
            <StatNumber>{summary.tier_b || 0}</StatNumber>
          </Stat>
          <Stat bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <StatLabel>Tier C</StatLabel>
            <StatNumber>{summary.tier_c || 0}</StatNumber>
          </Stat>
        </Grid>

        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" align={{ base: "stretch", md: "end" }} spacing={3} flexDirection={{ base: "column", md: "row" }}>
              <FormControl flex="1" minW={{ md: "360px" }}>
                <FormLabel {...filterLabelProps}>Search</FormLabel>
                <Input
                  {...filterInputProps}
                  placeholder="Company, address, website, phone, email, hub ID"
                  value={filters.q}
                  onChange={(event) => updateFilter("q", event.target.value)}
                />
              </FormControl>
              <HStack align="end" spacing={3}>
                <FormControl w={{ base: "full", md: "180px" }}>
                  <FormLabel {...filterLabelProps}>Sort</FormLabel>
                  <Select value={filters.sort} onChange={(event) => updateFilter("sort", event.target.value)} {...filterInputProps}>
                    <option value="score">Score</option>
                    <option value="tier">Tier</option>
                    <option value="next_follow_up">Next follow-up</option>
                    <option value="last_contacted">Last contacted</option>
                    <option value="recently_replied">Recently replied</option>
                    <option value="newest">Newest</option>
                    <option value="company_name">Company name</option>
                    <option value="last_checked">Last checked</option>
                  </Select>
                </FormControl>
                <Button size="sm" variant="ghost" onClick={resetFilters} isDisabled={activeFilterCount === 0}>
                  Clear{activeFilterCount > 0 ? ` ${activeFilterCount}` : ""}
                </Button>
              </HStack>
            </HStack>

            <SimpleGrid minChildWidth="170px" spacing={3}>
              <FormControl>
                <FormLabel {...filterLabelProps}>Campaign</FormLabel>
                <Select value={filters.campaign_id} onChange={(event) => updateFilter("campaign_id", event.target.value)} {...filterInputProps}>
                  <option value="">Any campaign</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Market</FormLabel>
                <Select value={filters.region} onChange={(event) => updateFilter("region", event.target.value)} {...filterInputProps}>
                  <option value="">Any region</option>
                  {regions.map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Campaign Industry</FormLabel>
                <Select value={filters.industry} onChange={(event) => updateFilter("industry", event.target.value)} {...filterInputProps}>
                  <option value="">Any industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{titleize(industry)}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Enriched Industry</FormLabel>
                <Select value={filters.enrichment_industry} onChange={(event) => updateFilter("enrichment_industry", event.target.value)} {...filterInputProps}>
                  <option value="">Any industry</option>
                  {(payload?.filters?.enrichment_industries || []).map((industry) => (
                    <option key={industry} value={industry}>{titleize(industry)}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Tier</FormLabel>
                <Select value={filters.lead_tier} onChange={(event) => updateFilter("lead_tier", event.target.value)} {...filterInputProps}>
                  <option value="">Any tier</option>
                  {(payload?.filters?.lead_tiers || []).map((tier) => (
                    <option key={tier} value={tier}>Tier {tier}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Sales Status</FormLabel>
                <Select value={filters.sales_status} onChange={(event) => updateFilter("sales_status", event.target.value)} {...filterInputProps}>
                  <option value="">Any status</option>
                  {(payload?.filters?.sales_statuses || []).map((status) => (
                    <option key={status} value={status}>{titleize(status)}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Primary Contact</FormLabel>
                <Select value={filters.has_primary_contact} onChange={(event) => updateFilter("has_primary_contact", event.target.value)} {...filterInputProps}>
                  <option value="">Any</option>
                  <option value="true">Has primary</option>
                  <option value="false">No primary</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Contact Quality</FormLabel>
                <Select value={filters.contact_quality} onChange={(event) => updateFilter("contact_quality", event.target.value)} {...filterInputProps}>
                  <option value="">Any quality</option>
                  {(payload?.filters?.contact_qualities || []).map((quality) => (
                    <option key={quality} value={quality}>{titleize(quality)}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Last Contacted</FormLabel>
                <Select value={filters.last_contacted} onChange={(event) => updateFilter("last_contacted", event.target.value)} {...filterInputProps}>
                  <option value="">Any</option>
                  <option value="any">Contacted</option>
                  <option value="never">Never contacted</option>
                  <option value="last_7_days">Last 7 days</option>
                  <option value="last_30_days">Last 30 days</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Follow-up</FormLabel>
                <Select value={filters.follow_up} onChange={(event) => updateFilter("follow_up", event.target.value)} {...filterInputProps}>
                  <option value="">Any</option>
                  <option value="needs_follow_up">Needs follow-up</option>
                  <option value="overdue_follow_up">Overdue</option>
                  <option value="none">No follow-up set</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Contact State</FormLabel>
                <Select value={filters.never_contacted} onChange={(event) => updateFilter("never_contacted", event.target.value)} {...filterInputProps}>
                  <option value="">Any</option>
                  <option value="true">Never contacted</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Score Signals</FormLabel>
                <Select value={filters.multiple_crews} onChange={(event) => updateFilter("multiple_crews", event.target.value)} {...filterInputProps}>
                  <option value="">Any crews</option>
                  <option value="true">Multiple crews</option>
                  <option value="false">No multiple crews</option>
                  <option value="unknown">Unknown crews</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Field Staff</FormLabel>
                <Select value={filters.field_staff_min} onChange={(event) => updateFilter("field_staff_min", event.target.value)} {...filterInputProps}>
                  <option value="">Any size</option>
                  <option value="3">3+ field staff</option>
                  <option value="5">5+ field staff</option>
                  <option value="10">10+ field staff</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Website</FormLabel>
                <Select value={filters.website} onChange={(event) => updateFilter("website", event.target.value)} {...filterInputProps}>
                  <option value="">Any website</option>
                  <option value="present">Has website</option>
                  <option value="missing">Missing website</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Jobber</FormLabel>
                <Select value={filters.jobber} onChange={(event) => updateFilter("jobber", event.target.value)} {...filterInputProps}>
                  <option value="">Any status</option>
                  <option value="true">Mentions Jobber</option>
                  <option value="false">No Jobber mention</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>ServiceM8</FormLabel>
                <Select value={filters.service_m8} onChange={(event) => updateFilter("service_m8", event.target.value)} {...filterInputProps}>
                  <option value="">Any status</option>
                  <option value="true">Mentions ServiceM8</option>
                  <option value="false">No ServiceM8 mention</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Scan</FormLabel>
                <Select value={filters.detection_status} onChange={(event) => updateFilter("detection_status", event.target.value)} {...filterInputProps}>
                  <option value="">Any status</option>
                  {(payload?.filters?.detection_statuses || []).map((status) => (
                    <option key={status} value={status}>{titleize(status)}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel {...filterLabelProps}>Enrichment</FormLabel>
                <Select value={filters.enrichment_status} onChange={(event) => updateFilter("enrichment_status", event.target.value)} {...filterInputProps}>
                  <option value="">Any status</option>
                  {(payload?.filters?.enrichment_statuses || []).map((status) => (
                    <option key={status} value={status}>{titleize(status)}</option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </Box>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Company</Th>
                <Th>Website</Th>
                <Th>Contact</Th>
                <Th>Email</Th>
                <Th>Jobber</Th>
                <Th>Score</Th>
                <Th>Sales</Th>
                <Th>Industry</Th>
                <Th>Field Staff</Th>
                <Th>Ops</Th>
                <Th>Reviews</Th>
                <Th>Enrichment</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {leads.map((lead) => (
                (() => {
                  const websiteUrl = normalizedHttpUrl(lead.website_url);

                  return (
                    <React.Fragment key={lead.id}>
                    <Tr>
                      <Td minW="260px">
                        <Text fontWeight="semibold">{lead.company_name}</Text>
                        <Text fontSize="xs" color="gray.600">{lead.formatted_address || "No address"}</Text>
                        {lead.phone_number && <Text fontSize="xs" color="gray.600">{lead.phone_number}</Text>}
                      </Td>
                      <Td minW="220px">
                        {websiteUrl ? (
                          <Link href={websiteUrl} color="blue.600" isExternal>
                            {displayUrl(websiteUrl)} <ExternalLink size={12} style={{ display: "inline" }} />
                          </Link>
                        ) : lead.website_url ? (
                          <Text color="gray.600" noOfLines={1}>{lead.website_url}</Text>
                        ) : (
                          <Text color="gray.500">No website</Text>
                        )}
                      </Td>
                      <Td minW="210px">
                        {lead.primary_contact ? (
                          <>
                            <Text fontWeight="semibold">{lead.primary_contact.display_name}</Text>
                            <Text fontSize="xs" color="gray.600">{lead.primary_contact.job_title || "No title"}</Text>
                            <Badge mt={1} size="sm" colorScheme={lead.primary_contact.contact_quality === "strong" ? "green" : lead.primary_contact.contact_quality === "medium" ? "blue" : "gray"}>
                              {titleize(lead.primary_contact.contact_quality || "unknown")}
                            </Badge>
                          </>
                        ) : (
                          <Text color="gray.500">No primary contact</Text>
                        )}
                      </Td>
                      <Td minW="220px">
                        {lead.email ? (
                          <Link href={`mailto:${lead.best_outreach_email || lead.email}`} color="blue.600" noOfLines={1}>
                            {lead.best_outreach_email || lead.email}
                          </Link>
                        ) : lead.best_outreach_email ? (
                          <Link href={`mailto:${lead.best_outreach_email}`} color="blue.600" noOfLines={1}>
                            {lead.best_outreach_email}
                          </Link>
                        ) : (
                          <Text color="gray.500">No email</Text>
                        )}
                        {lead.email && lead.best_outreach_email !== lead.email && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            Fallback: {lead.email}
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={lead.jobber_mentioned ? "green" : "gray"}>
                          {lead.jobber_mentioned ? "Mentioned" : "No mention"}
                        </Badge>
                        {lead.jobber_client_hub_id && (
                          <Text mt={1} fontSize="xs" color="gray.600" noOfLines={1}>
                            {lead.jobber_client_hub_id}
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={lead.lead_tier === "A" ? "green" : lead.lead_tier === "B" ? "blue" : "gray"}>
                          {lead.lead_tier ? `Tier ${lead.lead_tier}` : "Unscored"}
                        </Badge>
                        <Text mt={1} fontWeight="semibold">{optionalValue(lead.lead_score)}</Text>
                      </Td>
                      <Td minW="160px">
                        <Badge colorScheme={lead.sales_status === "interested" || lead.sales_status === "customer" ? "green" : lead.sales_status === "contacted" || lead.sales_status === "replied" ? "blue" : lead.sales_status === "bad_fit" || lead.sales_status === "not_interested" ? "red" : "gray"}>
                          {titleize(lead.sales_status || "uncontacted")}
                        </Badge>
                        <Text mt={1} fontSize="xs" color={lead.follow_up_overdue ? "red.600" : "gray.600"}>
                          Follow-up: {formatDateTime(lead.next_follow_up_at)}
                        </Text>
                        <Text fontSize="xs" color="gray.600">Last: {formatDateTime(lead.last_contacted_at)}</Text>
                      </Td>
                      <Td>
                        <Text>{lead.industry ? titleize(lead.industry) : "Unknown"}</Text>
                        <Text fontSize="xs" color="gray.600">{optionalValue(lead.locations_count)} locations</Text>
                      </Td>
                      <Td whiteSpace="nowrap">
                        <Text>{optionalValue(lead.field_staff_count_estimate)}</Text>
                        <Text fontSize="xs" color="gray.600">{optionalValue(lead.employee_count_estimate)} employees</Text>
                      </Td>
                      <Td minW="170px">
                        <Text fontSize="sm">Crews: {booleanLabel(lead.has_multiple_crews)}</Text>
                        <Text fontSize="xs" color="gray.600">Hiring: {booleanLabel(lead.is_hiring_field_staff)}</Text>
                        <Text fontSize="xs" color="gray.600">Dispatch: {booleanLabel(lead.has_dispatch_language)}</Text>
                      </Td>
                      <Td whiteSpace="nowrap">{optionalValue(lead.google_review_count)}</Td>
                      <Td minW="160px">
                        <Badge colorScheme={statusColor(lead.enrichment_status)}>
                          {titleize(lead.enrichment_status || "pending")}
                        </Badge>
                        <Text mt={1} fontSize="xs" color="gray.600">Confidence: {confidenceLabel(lead.enrichment_confidence)}</Text>
                        {lead.enrichment_error && (
                          <Text mt={1} color="red.600" fontSize="xs" maxW="220px" noOfLines={2}>
                            {lead.enrichment_error}
                          </Text>
                        )}
                      </Td>
                      <Td textAlign="right">
                        <HStack justify="flex-end">
                          <IconButton
                            as={RouterLink}
                            to={`/leads/${lead.id}`}
                            aria-label="Open lead"
                            icon={<Eye size={16} />}
                            size="sm"
                            variant="ghost"
                          />
                          <IconButton
                            aria-label="View enrichment evidence"
                            icon={expandedEvidenceId === lead.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            size="sm"
                            variant="ghost"
                            isDisabled={!lead.enrichment_evidence || Object.keys(lead.enrichment_evidence).length === 0}
                            onClick={() => setExpandedEvidenceId((current) => current === lead.id ? null : lead.id)}
                          />
                          <IconButton
                            aria-label="Enrich lead"
                            icon={enrichingId === lead.id ? <Spinner size="xs" /> : <Sparkles size={16} />}
                            size="sm"
                            variant="ghost"
                            isDisabled={!lead.jobber_mentioned || !websiteUrl || enrichingId === lead.id}
                            onClick={() => enrichLead(lead.id, lead.enrichment_status === "completed")}
                          />
                          <IconButton
                            aria-label="Rescan website"
                            icon={rescanningId === lead.id ? <Spinner size="xs" /> : <RefreshCw size={16} />}
                            size="sm"
                            variant="ghost"
                            isDisabled={!websiteUrl || rescanningId === lead.id}
                            onClick={() => rescanLead(lead.id)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                    {expandedEvidenceId === lead.id && (
                      <Tr>
                        <Td colSpan={13} bg="gray.50">
                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
                            {Object.entries(lead.enrichment_evidence || {}).map(([key, value]) => (
                              <Box key={key} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                                <Text fontSize="xs" fontWeight="semibold" color="gray.700">{titleize(key)}</Text>
                                <Text mt={1} fontSize="sm" color="gray.700">{value || "No evidence found."}</Text>
                              </Box>
                            ))}
                          </Grid>
                        </Td>
                      </Tr>
                    )}
                    </React.Fragment>
                  );
                })()
              ))}
              {leads.length === 0 && (
                <Tr>
                  <Td colSpan={13}>
                    <Text color="gray.600" textAlign="center" py={8}>No leads found.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        <HStack justify="space-between">
          <Text color="gray.600" fontSize="sm">
            Page {pagination.page || 1} of {pagination.total_pages || 1}
          </Text>
          <HStack>
            <Button size="sm" onClick={() => setPage((current) => Math.max(1, current - 1))} isDisabled={(pagination.page || 1) <= 1}>
              Previous
            </Button>
            <Button size="sm" onClick={() => setPage((current) => current + 1)} isDisabled={(pagination.page || 1) >= (pagination.total_pages || 1)}>
              Next
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
}
